import { gql } from "@apollo/client";

export const ADD_USER = gql`
  mutation addUser($input: AddUserInput) {
    addUser(input: $input) {
      firstName
      lastName
      userId
      id
      location {
        id
      }
    }
  }
`;

export const USER_PROFILE_FRAGMENT = gql`
  fragment UserProfileFields on User {
    id
    userId
    role
    birthdate
    company {
      id
      businessLegalName
      businessDBA
      businessCardName
      establishedDate
      businessEIN
      websiteURL
      businessIncorporationState
      typeOfBusiness
      phoneNumber
      applicationType
      description
      status
      companyCurrency
    }
    department {
      id
    }
    manager {
      id
      userId
      firstName
      lastName
      title
      role
    }
    firstName
    lastName
    title
    address {
      addressName
      address1
      address2
      zipcode
      state
      country
      city
    }
    isOwner
    status
    email
    phone
    profilePicUrl
    profilePicKey
    location {
      id
      addressName
      address1
      address2
      city
      state
      country
      zipcode
      status
    }
    manager {
      id
      firstName
      lastName
    }
    hasPhysicalCard
    hasPersonalBankAccount
    customFields {
      fieldSettingId
      values
    }
    sourceTerminalLocationId
  }
`;

export const GET_USER_BY_ID = gql`
  ${USER_PROFILE_FRAGMENT}
  query user($id: ID!) {
    userById(id: $id) {
      ...UserProfileFields
    }
  }
`;

// The signed-in person's own record: userById is Manager and above, but anyone may read themselves.
export const GET_MY_PROFILE = gql`
  ${USER_PROFILE_FRAGMENT}
  query myProfile {
    user {
      ...UserProfileFields
    }
  }
`;

export const GET_USER = gql`
  query user($userId: ID) {
    user(userId: $userId) {
      id
      role
      platformId
      company {
        id
        businessLegalName
        businessDBA
        businessCardName
        establishedDate
        businessEIN
        websiteURL
        businessIncorporationState
        typeOfBusiness
        phoneNumber
        applicationType
        description
        status
        companyCurrency
        acceptedTerms
        cardHolderType
      }
      address {
        addressName
        address1
        address2
        zipcode
        state
        city
        country
      }
      department {
        id
      }
      profilePicKey
      profilePicUrl
      firstName
      lastName
      title
      isOwner
      status
      email
      phone
      birthdate
      role
      location {
        id
      }
      acceptedTerms
    }
  }
`;

export const GET_USER_BY_EMAIL = gql`
  query userByEmail($email: String!) {
    userByEmail(email: $email) {
      id
      role
      company {
        id
        businessLegalName
        businessDBA
        businessCardName
        establishedDate
        businessEIN
        websiteURL
        businessIncorporationState
        typeOfBusiness
        phoneNumber
        applicationType
        description
        status
        companyCurrency
      }
      address {
        addressName
        address1
        address2
        zipcode
        state
        city
        country
      }
      department {
        id
      }
      profilePicKey
      profilePicUrl
      firstName
      lastName
      title
      isOwner
      status
      email
      phone
      role
      location {
        id
      }
      acceptedTerms
      customFields {
        fieldSettingId
        values
      }
    }
  }
`;

/**
 * Just enough to label a person in a filter. The full `users` node pulls
 * company, department, cards, address and custom fields, none of which a
 * dropdown label needs.
 */
export const GET_USER_FILTER_OPTIONS = gql`
  query usersFilterOptions($first: Int, $status: [StatusEnum]) {
    users(first: $first, status: $status) {
      edges {
        node {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const GET_USERS = gql`
  query users(
    $first: Int
    $after: String
    $before: String
    $email: String
    $isOwner: Boolean
    $title: TitleEnum
    $role: [RoleEnum]
    $status: [StatusEnum]
    $firstName: String
    $searchTerm: String
    $companyId: ID
  ) {
    users(
      first: $first
      after: $after
      before: $before
      email: $email
      isOwner: $isOwner
      title: $title
      role: $role
      status: $status
      firstName: $firstName
      searchTerm: $searchTerm
      companyId: $companyId
    ) {
      edges {
        cursor
        node {
          id
          userId
          company {
            id
            businessLegalName
          }
          department {
            id
          }
          cards {
            id
          }
          address {
            addressName
            address1
            address2
            zipcode
            state
            city
            country
          }
          title
          firstName
          middleName
          lastName
          profilePicUrl
          birthdate
          role
          isOwner
          email
          phone
          status
          hasPhysicalCard
          hasPersonalBankAccount
          location {
            id
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const USER_OPTIONS = gql`
  query userOptions(
    $first: Int
    $after: String
    $status: [StatusEnum]
    $searchTerm: String
  ) {
    users(
      first: $first
      after: $after
      status: $status
      searchTerm: $searchTerm
    ) {
      edges {
        node {
          id
          firstName
          lastName
          email
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

export const GET_PLATFORM_USERS = gql`
  query platformUsers(
    $first: Int
    $after: String
    $before: String
    $email: String
    $isOwner: Boolean
    $title: TitleEnum
    $role: [RoleEnum]
    $status: [StatusEnum]
    $firstName: String
    $searchTerm: String
    $companyId: ID
  ) {
    platformUsers(
      first: $first
      after: $after
      before: $before
      email: $email
      isOwner: $isOwner
      title: $title
      role: $role
      status: $status
      firstName: $firstName
      searchTerm: $searchTerm
      companyId: $companyId
    ) {
      edges {
        cursor
        node {
          id
          userId
          company {
            id
            businessLegalName
          }
          department {
            id
          }
          address {
            addressName
            address1
            address2
            zipcode
            state
            city
            country
          }
          title
          firstName
          middleName
          lastName
          profilePicUrl
          birthdate
          role
          isOwner
          email
          phone
          status
        }
      }
      pageInfo {
        hasNextPage
        endCursor
        total
      }
    }
  }
`;

export const GET_USER_BANK_ACCOUNT_STATUS = gql`
  query userBankAccountStatus($id: ID!) {
    userById(id: $id) {
      id
      hasPersonalBankAccount
    }
  }
`;

export const MY_ANALYTIC_TRANSACTION_SETTINGS = gql`
  query myAnalyticTransactionSettings {
    myAnalyticTransactionSettings {
      id
      name
      settingData {
        key
        name
        parentId
        parentName
        index
      }
    }
  }
`;

export const DEFAULT_ANALYTIC_TRANSACTION_SETTINGS = gql`
  query defaultAnalyticTransactionSettings {
    defaultAnalyticTransactionSettings {
      key
      name
      parentId
      parentName
      index
    }
  }
`;

export const REACTIVATE_USER = gql`
  mutation reactivateUser($id: ID!) {
    reactivateUser(id: $id) {
      id
      status
    }
  }
`;

export const UPDATE_USER = gql`
  mutation updateUser($id: ID!, $input: UpdateUserInput) {
    updateUser(id: $id, input: $input) {
      id
    }
  }
`;

export const UPDATE_MY_PROFILE = gql`
  mutation updateMyProfile($input: UpdateMyProfileInput!) {
    updateMyProfile(input: $input) {
      id
    }
  }
`;

export const UPDATE_PASSWORD = gql`
  mutation updatePassword($id: ID!, $input: UpdatePasswordInput) {
    updatePassword(id: $id, input: $input) {
      id
    }
  }
`;

export const REMOVE_USER = gql`
  mutation removeUser($id: ID!) {
    removeUser(id: $id) {
      id
    }
  }
`;

export const LOCK_USER = gql`
  mutation lockUser($id: ID!, $input: LockUserInput) {
    lockUser(id: $id, input: $input) {
      id
    }
  }
`;

export const UNLOCK_USER = gql`
  mutation unlockUser($id: ID!, $input: UnlockUserInput) {
    unlockUser(id: $id, input: $input) {
      id
    }
  }
`;

export const ACCEPT_TERMS = gql`
  mutation acceptTerms {
    acceptTerms {
      id
    }
  }
`;

export const RESEND_INVITE_EMAIL = gql`
  mutation resendUserInviteEmail($email: String!) {
    resendUserInviteEmail(email: $email) {
      success
    }
  }
`;

export const RESET_USER_PASSWORD = gql`
  mutation resetUserPassword($email: String!) {
    resetUserPassword(email: $email) {
      success
    }
  }
`;

export const CHECK_ACTIVE_ADMINS = gql`
  query checkActiveAdmins {
    checkActiveAdmins
  }
`;

export const UPDATE_USER_SETTINGS = gql`
  mutation updateUserSettings(
    $userSettingType: UserSettingType!
    $input: TransactionAnalyticInput!
  ) {
    updateUserSettings(userSettingType: $userSettingType, input: $input) {
      id
      name
      settingData {
        key
        name
        parentId
        parentName
        index
      }
    }
  }
`;

export const ADD_USER_SETTINGS = gql`
  mutation addUserSettings(
    $userSettingType: UserSettingType!
    $input: TransactionAnalyticInput!
  ) {
    addUserSettings(userSettingType: $userSettingType, input: $input) {
      id
      name
      settingData {
        key
        name
        parentId
        parentName
        index
      }
    }
  }
`;
export const REMOVE_USER_SETTINGS = gql`
  mutation removeUserSettings(
    $userSettingType: UserSettingType!
    $input: TransactionAnalyticInput!
  ) {
    removeUserSettings(userSettingType: $userSettingType, input: $input)
  }
`;

export const SEND_PERSONAL_BANK_ACCOUNT_EMAIL = gql`
  mutation sendPersonalBankAccountEmail($id: ID!) {
    sendPersonalBankAccountEmail(id: $id) {
      isSuccess
    }
  }
`;

export const GET_MENTIONABLE_USERS = gql`
  query mentionableUsers {
    mentionableUsers {
      id
      firstName
      lastName
      profilePicUrl
    }
  }
`;

// What a UserAvatar draws.
export const USER_AVATAR_FIELDS = gql`
  fragment UserAvatarFields on User {
    id
    firstName
    lastName
    profilePicUrl
  }
`;

export const REMOVE_USER_PROFILE_PICTURE = gql`
  mutation removeUserProfilePicture($id: ID) {
    removeUserProfilePicture(id: $id) {
      ...UserAvatarFields
    }
  }
  ${USER_AVATAR_FIELDS}
`;
